export function registerLatentLoader() {
  if (
    typeof window === 'undefined' ||
    !window.customElements ||
    customElements.get('peatch-loader')
  ) {
    return
  }
  const instances = new Set()
  const motion = matchMedia('(prefers-reduced-motion: reduce)')
  const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x))
  const ease = (x) => {
    const progress = clamp(x)
    return progress * progress * progress * (progress * (progress * 6 - 15) + 10)
  }
  let frame = 0
  let last = 0
  let time = 0
  let manualPause = false
  function schedule() {
    if (
      !frame &&
      !document.hidden &&
      !motion.matches &&
      !manualPause &&
      [...instances].some((p) => p.visible && p.needsFrame())
    ) {
      frame = requestAnimationFrame(tick)
    }
  }
  function tick(now) {
    frame = 0
    if (last) {
      time += Math.min((now - last) / 1000, 0.05)
    }
    last = now
    for (const p of instances) {
      if (p.visible && p.needsFrame()) {
        p.draw(time)
      }
    }
    schedule()
  }
  function refresh() {
    cancelAnimationFrame(frame)
    frame = 0
    last = 0
    for (const p of instances) {
      p.lastClock = null
      p.refreshColor()
      p.draw(time)
    }
    schedule()
  }
  const observer =
    typeof IntersectionObserver === 'undefined'
      ? {
          observe(p) {
            p.visible = true
            schedule()
          },
          unobserve() {
            /* No observer to disconnect in this fallback. */
          },
        }
      : new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              const p = e.target
              p.visible = e.isIntersecting
              p.lastClock = null
              if (p.visible) {
                p.draw(time)
              }
            }
            schedule()
          },
          {
            rootMargin: '30px',
          }
        )
  class PeatchLoader extends HTMLElement {
    static get observedAttributes() {
      return ['size', 'tone', 'theme', 'paused', 'speed', 'state', 'shape']
    }
    constructor() {
      super()
      this.visible = false
      this.age = 0
      this.localTime = 0
      this.lastClock = null
      this.blend = 0
      this.from = 0
      this.target = 0
      this.blendStart = 0
      this.aimX = 0
      this.aimY = 0
      this.lookX = 0
      this.lookY = 0
      const root = this.attachShadow({
        mode: 'open',
      })
      root.innerHTML =
        '<style>:host{display:inline-flex;align-items:center;justify-content:center;width:var(--loader-size,24px);height:var(--loader-size,24px);flex:none;vertical-align:middle;line-height:0;contain:layout paint}:host(:not([data-canvas]))::before{content:"";width:60%;height:60%;border:2px dotted currentColor;border-radius:50%}canvas{display:block;width:100%;height:100%}</style><canvas aria-hidden="true"></canvas>'
      this.canvas = root.querySelector('canvas')
      this.ctx = null
      try {
        if (typeof CanvasRenderingContext2D !== 'undefined') {
          this.ctx = this.canvas.getContext('2d')
        }
      } catch {
        // A dotted CSS fallback remains visible without Canvas.
      }
      this.addEventListener('pointermove', (e) => {
        if (!this.hasAttribute('interactive') || e.pointerType === 'touch') {
          return
        }
        const r = this.getBoundingClientRect()
        this.aimX = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1)
        this.aimY = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1)
        schedule()
      })
      this.addEventListener('pointerleave', () => {
        this.aimX = 0
        this.aimY = 0
        schedule()
      })
    }
    connectedCallback() {
      if (this.ctx) {
        this.setAttribute('data-canvas', 'ready')
      }
      if (!this.hasAttribute('aria-hidden')) {
        this.setAttribute('aria-hidden', 'true')
      }
      instances.add(this)
      this.target = this.getAttribute('state') === 'success' ? 1 : 0
      this.resize()
      observer.observe(this)
      schedule()
    }
    disconnectedCallback() {
      observer.unobserve(this)
      instances.delete(this)
      this.lastClock = null
      if (!instances.size) {
        cancelAnimationFrame(frame)
        frame = 0
        last = 0
      }
    }
    attributeChangedCallback(name) {
      if (!this.isConnected) {
        return
      }
      if (name === 'state') {
        this.from = this.blend
        this.target = this.getAttribute('state') === 'success' ? 1 : 0
        this.blendStart = this.localTime
        this.lastClock = null
        this.draw(time)
      } else if (name === 'size') {
        this.resize()
      } else {
        this.refreshColor()
        this.draw(time)
      }
      schedule()
    }
    needsFrame() {
      return (
        Boolean(this.ctx) &&
        !this.hasAttribute('paused') &&
        (this.target === 0 ||
          Math.abs(this.blend - this.target) > 0.001 ||
          Math.abs(this.lookX - this.aimX) + Math.abs(this.lookY - this.aimY) > 0.001)
      )
    }
    replay() {
      this.age = 0
      this.lastClock = null
      this.from = this.blend
      this.target = 0
      this.blendStart = this.localTime
      this.setAttribute('state', 'loading')
      this.draw(time)
      schedule()
    }
    resize() {
      this.size = clamp(Number(this.getAttribute('size')) || 24, 16, 512)
      this.style.setProperty('--loader-size', `${this.size}px`)
      const dpr = Math.min(devicePixelRatio || 1, 2)
      this.canvas.width = Math.round(this.size * dpr)
      this.canvas.height = Math.round(this.size * dpr)
      this.refreshColor()
      this.draw(time)
    }
    refreshColor() {
      const style = getComputedStyle(this)
      this.color = style.color
      this.palette = [
        '--color-primary-700',
        '--color-primary-600',
        '--color-primary-500',
        '--color-secondary-500',
        '--color-secondary-600',
      ].map((token) => style.getPropertyValue(token).trim() || this.color)
      this.mono = this.getAttribute('tone') === 'current'
    }
    drawCompact(t, mix, still) {
      const c = this.ctx
      c.setTransform(this.canvas.width / 100, 0, 0, this.canvas.height / 100, 0, 0)
      c.clearRect(0, 0, 100, 100)
      c.lineWidth = 10
      c.lineCap = 'round'
      c.lineJoin = 'round'
      if (mix < 1) {
        for (let i = 0; i < 5; i++) {
          const phase = (((t / 1.5 - i * 0.1) % 1) + 1) % 1
          const turn = still
            ? 0
            : phase < 0.2
              ? 0
              : phase < 0.64
                ? ease((phase - 0.2) / 0.44)
                : phase < 0.72
                  ? 1
                  : 1 - ease((phase - 0.72) / 0.28)
          const angle = (turn * Math.PI) / 2
          const x = 20 + i * 15 + this.lookX * 1.5
          const y = 50 + turn * 3 + this.lookY * 1.5
          const dx = Math.sin(angle) * 13
          const dy = Math.cos(angle) * 13
          c.globalAlpha = (0.85 + (1 - turn) * 0.15) * (1 - mix)
          c.strokeStyle = this.mono ? this.color : this.palette[i]
          c.beginPath()
          c.moveTo(x - dx, y - dy)
          c.lineTo(x + dx, y + dy)
          c.stroke()
        }
      }
      if (mix > 0) {
        c.globalAlpha = mix
        c.strokeStyle = this.color
        c.beginPath()
        c.moveTo(28, 49)
        c.lineTo(44, 65)
        c.lineTo(73, 31)
        c.stroke()
      }
      c.globalAlpha = 1
    }
    drawRadial(t, mix, still) {
      const c = this.ctx
      c.setTransform(this.canvas.width / 100, 0, 0, this.canvas.height / 100, 0, 0)
      c.clearRect(0, 0, 100, 100)
      c.lineWidth = 12
      c.lineCap = 'round'
      c.lineJoin = 'round'
      if (mix < 1) {
        const spokes = 8
        const lead = still ? 0 : (((t % 1) + 1) % 1) * spokes
        for (let i = 0; i < spokes; i++) {
          const behind = (((lead - i) % spokes) + spokes) % spokes
          const angle = (i / spokes) * Math.PI * 2 - Math.PI / 2
          const x = Math.cos(angle)
          const y = Math.sin(angle)
          c.globalAlpha = (1 - (behind / spokes) * 0.75) * (1 - mix)
          c.strokeStyle = this.mono ? this.color : this.palette[Math.floor((i * 5) / spokes)]
          c.beginPath()
          c.moveTo(50 + x * 22, 50 + y * 22)
          c.lineTo(50 + x * 40, 50 + y * 40)
          c.stroke()
        }
      }
      if (mix > 0) {
        c.globalAlpha = mix
        c.strokeStyle = this.color
        c.beginPath()
        c.moveTo(28, 49)
        c.lineTo(44, 65)
        c.lineTo(73, 31)
        c.stroke()
      }
      c.globalAlpha = 1
    }
    draw(clock) {
      const c = this.ctx
      if (!c || !this.size) {
        return
      }
      if (this.mono) {
        this.color = getComputedStyle(this).color
      }
      const still = motion.matches || this.hasAttribute('paused')
      const dt = this.lastClock === null ? 0 : clamp(clock - this.lastClock, 0, 0.05)
      this.lastClock = clock
      if (!still && !manualPause) {
        this.age += dt
        this.localTime += dt
      }
      this.blend = still
        ? this.target
        : this.from + (this.target - this.from) * ease((this.localTime - this.blendStart) / 0.86)
      if (Math.abs(this.blend - this.target) < 0.001) {
        this.blend = this.target
      }
      const mix = this.blend
      const lag = still ? 1 : 1 - Math.exp(-dt * 7)
      this.lookX += (this.aimX - this.lookX) * lag
      this.lookY += (this.aimY - this.lookY) * lag
      const t = this.localTime * clamp(Number(this.getAttribute('speed')) || 1, 0.1, 3)
      if (this.getAttribute('shape') === 'radial') {
        this.drawRadial(t, mix, still)
      } else {
        this.drawCompact(t, mix, still)
      }
    }
  }

  customElements.define('peatch-loader', PeatchLoader)
  document.addEventListener('visibilitychange', refresh)
  motion.addEventListener('change', refresh)
  document.addEventListener('transitionend', (event) => {
    if (event.propertyName === 'color') {
      refresh()
    }
  })
  new MutationObserver(refresh).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style', 'class'],
  })
  new MutationObserver(refresh).observe(document.documentElement, {
    attributes: true,
    subtree: true,
    attributeFilter: ['data-theme'],
  })
  window.addEventListener('resize', () => {
    for (const p of instances) {
      p.resize()
    }
  })
  window.PeatchLoaders = {
    pause(value = true) {
      manualPause = value
      refresh()
    },
    refresh,
  }
}
